import React from "react";
import { GlobalContext } from "../../context/GlobalContext";
import { VIDEO_PROMPT_TYPES } from "../../constants/promptTemplateTypes";
import {
  getDefaultPromptFromTemplates,
  getVideoPromptDbTypeForModel,
  mapDbRowToPickerItem,
} from "./videoPromptTemplateUtils";

const VideoPromptTemplatesContext = React.createContext(null);

export function VideoPromptTemplatesProvider({ children }) {
  const { hitAxios } = React.useContext(GlobalContext);
  const [rowsByType, setRowsByType] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await hitAxios({
        path: "/api/admin/get_prompt_t",
        post: false,
        admin: false,
      });
      if (!res?.data?.success) {
        setError(res?.data?.msg || "Could not load prompt templates");
        setRowsByType({});
        return;
      }
      const all = res.data.data || [];
      const grouped = {};
      for (const type of VIDEO_PROMPT_TYPES) {
        grouped[type] = [];
      }
      for (const row of all) {
        if (VIDEO_PROMPT_TYPES.includes(row.type)) {
          grouped[row.type].push(row);
        }
      }
      setRowsByType(grouped);
    } catch (e) {
      setError(e?.message || "Could not load prompt templates");
      setRowsByType({});
    } finally {
      setLoading(false);
    }
  }, [hitAxios]);

  React.useEffect(() => {
    load();
  }, [load]);

  const getTemplatesForModel = React.useCallback(
    (modelId) => {
      const dbType = getVideoPromptDbTypeForModel(modelId);
      const rows = rowsByType[dbType] || [];
      return rows.map(mapDbRowToPickerItem);
    },
    [rowsByType],
  );

  const getDefaultPromptForModel = React.useCallback(
    (modelId) => getDefaultPromptFromTemplates(getTemplatesForModel(modelId)),
    [getTemplatesForModel],
  );

  const value = React.useMemo(
    () => ({
      loading,
      error,
      reload: load,
      getTemplatesForModel,
      getDefaultPromptForModel,
    }),
    [loading, error, load, getTemplatesForModel, getDefaultPromptForModel],
  );

  return (
    <VideoPromptTemplatesContext.Provider value={value}>
      {children}
    </VideoPromptTemplatesContext.Provider>
  );
}

export function useVideoPromptTemplates() {
  const ctx = React.useContext(VideoPromptTemplatesContext);
  if (!ctx) {
    throw new Error("useVideoPromptTemplates must be used within VideoPromptTemplatesProvider");
  }
  return ctx;
}
